/**
 * @file IntegrationsPage.tsx
 * @description Управление внешними интеграциями платформы (phase08.5 shadcn;
 * phase19.29 — заголовок в верхней панели, слова владельца вместо жаргона,
 * удаление через окно кабинета).
 *
 * Страница = заголовок (`CabinetPageHeader`, главное действие «Подключить
 * интеграцию») + карточка на каждый вид интеграции со списком экземпляров
 * (`IntegrationKindCard` → `IntegrationInstanceRow`). После мутаций список перечитывается.
 *
 * phase19.28 этап А: раздел называется «Общие интеграции» (настройки
 * конкретного магазина — во вкладке его карточки); сверху — полоса переноса
 * общих настроек видов «только своя» в магазин (PL13).
 */

import { Plus } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { IntegrationDeleteDialog } from "../../components/platform/IntegrationDeleteDialog";
import { IntegrationEditDialog } from "../../components/platform/IntegrationEditDialog";
import { IntegrationKindCard } from "../../components/platform/IntegrationKindCard";
import { IntegrationTransferStrip } from "../../components/platform/shops/IntegrationTransferStrip";
import { useIntegrationTransfer } from "../../components/platform/shops/useIntegrationTransfer";
import { Alert } from "../../components/ui/alert";
import { Button } from "../../components/ui/button";
import { CabinetPageHeader } from "../../components/ui/CabinetPageHeader";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import { useNotify } from "../../components/ui/NotifyProvider";
import { Skeleton } from "../../components/ui/skeleton";
import { ApiError } from "../../lib/api-client";
import {
  platformIntegrations,
  INTEGRATION_KIND_META,
  INTEGRATION_KIND_ORDER,
  type PlatformIntegrationDto,
  type PlatformIntegrationKind,
} from "../../lib/api-platform-integrations";

const KIND_OPTIONS = INTEGRATION_KIND_ORDER;

export function IntegrationsPage() {
  const notify = useNotify();
  const [items, setItems] = useState<PlatformIntegrationDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [editing, setEditing] = useState<PlatformIntegrationDto | null>(null);
  const [creatingKind, setCreatingKind] = useState<PlatformIntegrationKind | null>(null);
  const [expandedUsageId, setExpandedUsageId] = useState<string | null>(null);
  const [testingId, setTestingId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<PlatformIntegrationDto | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const transfer = useIntegrationTransfer(() => void refresh());

  async function refresh() {
    setLoading(true);
    setError(null);
    try {
      setItems(await platformIntegrations.list());
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Не удалось загрузить интеграции");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  const groupedByKind = useMemo(() => {
    const map = new Map<PlatformIntegrationKind, PlatformIntegrationDto[]>();
    for (const k of KIND_OPTIONS) map.set(k, []);
    for (const item of items) map.set(item.kind, [...(map.get(item.kind) ?? []), item]);
    return map;
  }, [items]);

  /** phase19.5 — живая проверка подключения (баланс/OAuth/SMTP verify). */
  async function handleTest(item: PlatformIntegrationDto) {
    setTestingId(item.id);
    try {
      const result = await platformIntegrations.test(item.id);
      const text = `${result.message} (${result.latencyMs} мс)`;
      if (result.ok) notify.success(text);
      else notify.error(text);
      await refresh();
    } catch (err) {
      notify.error(err instanceof ApiError ? err.message : "Проверка не удалась");
    } finally {
      setTestingId(null);
    }
  }

  async function confirmDelete() {
    if (!deleting) return;
    setDeleteBusy(true);
    try {
      await platformIntegrations.remove(deleting.id);
      notify.success("Интеграция удалена");
      setDeleting(null);
      await refresh();
    } catch (err) {
      notify.error(err instanceof ApiError ? err.message : "Не удалось удалить интеграцию");
    } finally {
      setDeleteBusy(false);
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <CabinetPageHeader
        title="Общие интеграции"
        subtitle="Общие ключи платформы и сводка настроек магазинов. Ключи хранятся в зашифрованном виде."
        action={
          <DropdownMenu>
            <DropdownMenuTrigger
              render={(props) => (
                // Главное действие экрана — градиент бренда.
                <Button {...props} variant="brand">
                  <Plus className="size-4" />
                  Подключить интеграцию
                </Button>
              )}
            />
            <DropdownMenuContent align="end" sideOffset={6}>
              {KIND_OPTIONS.map((kind) => (
                <DropdownMenuItem key={kind} onClick={() => setCreatingKind(kind)}>
                  {INTEGRATION_KIND_META[kind].label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        }
      />

      {error ? <Alert severity="error">{error}</Alert> : null}
      {loading ? null : <IntegrationTransferStrip items={items} onTransfer={transfer.start} />}

      {loading ? (
        <div className="flex flex-col gap-3">
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {KIND_OPTIONS.map((kind) => (
            <IntegrationKindCard
              key={kind}
              kind={kind}
              items={groupedByKind.get(kind) ?? []}
              expandedUsageId={expandedUsageId}
              testingId={testingId}
              onAdd={() => setCreatingKind(kind)}
              onEdit={setEditing}
              onTest={(item) => void handleTest(item)}
              onToggleUsage={(item) =>
                setExpandedUsageId(expandedUsageId === item.id ? null : item.id)
              }
              onDelete={setDeleting}
              onTransfer={transfer.start}
            />
          ))}
        </div>
      )}

      {(creatingKind || editing) && (
        <IntegrationEditDialog
          open
          kind={editing ? editing.kind : (creatingKind as PlatformIntegrationKind)}
          integration={editing}
          onClose={() => {
            setCreatingKind(null);
            setEditing(null);
          }}
          onSaved={() => {
            setCreatingKind(null);
            setEditing(null);
            void refresh();
          }}
        />
      )}

      {transfer.dialog}
      <IntegrationDeleteDialog
        item={deleting}
        busy={deleteBusy}
        onCancel={() => setDeleting(null)}
        onConfirm={() => void confirmDelete()}
      />
    </div>
  );
}
